import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './Modal.scss';
import initModalRoot from './initModalRoot';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

export default function Modal({ isOpen, onClose, title, children, className = '' }: Props) {
  // ensure a modal root exists for portal
  initModalRoot();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        // basic focus trap
        const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    if (isOpen) {
      lastFocused.current = document.activeElement as HTMLElement | null;
      document.addEventListener('keydown', handleKey);
      // move focus into modal
      setTimeout(() => {
        const focusable = panelRef.current?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('keydown', handleKey);
      // restore focus
      try {
        lastFocused.current?.focus();
      } catch (e) {
        // ignore
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modal = (
    <div className={`ks-modal ${className}`} role="dialog" aria-modal="true">
      <div className="ks-modal__backdrop" onClick={onClose} />
      <div className="ks-modal__panel" ref={panelRef}>
        {title && <header className="ks-modal__header">{title}</header>}
        <div className="ks-modal__body">{children}</div>
      </div>
    </div>
  );

  const root = document.getElementById('modal-root') || document.body;
  return ReactDOM.createPortal(modal, root);
}
